import React, { useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useSwipeGesture } from '../../hooks/useSwipeGesture';
import { cn } from '../../utils/cn';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  position?: 'left' | 'right';
  children: React.ReactNode;
  className?: string;
  /** Custom width class (default: w-80 on mobile, w-96 on tablet) */
  widthClass?: string;
}

/**
 * Drawer component with slide animation and swipe-to-close gesture
 * Renders in a portal for proper z-index layering
 * Features:
 * - Backdrop overlay with click-to-close
 * - Swipe gesture support (left/right based on position)
 * - Keyboard support (ESC to close)
 * - Focus trap for accessibility
 * - Hardware-accelerated animations
 */
export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  position = 'right',
  children,
  className,
  widthClass = 'w-80 md:w-96',
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  // Setup swipe gesture based on drawer position
  const swipeOptions = {
    [position === 'right' ? 'onSwipeRight' : 'onSwipeLeft']: onClose,
    threshold: 50,
    preventDefaultTouchMove: true,
  };

  useSwipeGesture(drawerRef, swipeOptions);

  // Close on ESC key
  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  // Focus trap
  useEffect(() => {
    if (!open || !drawerRef.current) return;

    const drawer = drawerRef.current;
    const focusableElements = drawer.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus first element when drawer opens
    firstElement.focus();

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [open]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [open]);

  // Don't render anything if drawer is closed
  if (!open) return null;

  const drawerContent = (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className={cn(
          'fixed inset-0 bg-black transition-opacity duration-300',
          open ? 'opacity-50' : 'opacity-0'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        className={cn(
          'fixed inset-y-0 flex flex-col bg-gray-900 shadow-2xl',
          'transform-gpu transition-transform duration-300 ease-out',
          widthClass,
          position === 'right' ? 'right-0' : 'left-0',
          open
            ? 'translate-x-0'
            : position === 'right'
            ? 'translate-x-full'
            : '-translate-x-full',
          className
        )}
        style={{
          // Use will-change for better performance during animation
          willChange: open ? 'transform' : 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );

  // Render in portal (append to body)
  return ReactDOM.createPortal(drawerContent, document.body);
};
